import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  MenuItem,
  Chip,
  Link,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import WatchOutlinedIcon from "@mui/icons-material/WatchOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { filledFieldWrapSx } from "./listNewAssetFilledSurface";

const TEAL = "#00bfa5";
const TEAL_HOVER = "#00a68c";

const ENERGY_TYPES = [
  "Solar PV",
  "Wind",
  "Hydro",
  "Biomass",
  "Geothermal",
  "Energy Storage",
  "Other",
];

const CAPACITY_UNITS = ["MW", "kW"];

const COMMODITY_TYPES = [
  "Gold",
  "Silver",
  "Platinum",
  "Palladium",
  "Rare Earth Metals",
  "Other",
];

const COMMODITY_UNITS = ["kg", "oz", "tons", "g"];

const LUXURY_CONDITIONS = [
  "Mint/Unworn",
  "Excellent",
  "Good",
  "Fair",
];

const labelSx = { fontWeight: 600, fontSize: 14, color: "#111827", mb: 0.75 };

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "#ef4444" }}>
        *
      </Box>
    </Typography>
  );
}

function OptionalLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}
    </Typography>
  );
}

function categoryChipMeta(title) {
  const t = String(title || "").trim();
  if (t === "Renewable Energy") {
    return {
      Icon: ElectricBoltOutlinedIcon,
      chipSx: {
        bgcolor: "#d1fae5",
        color: "#047857",
        "& .MuiChip-icon": { ml: "6px", color: "#047857 !important" },
      },
    };
  }
  if (t === "Commodities") {
    return {
      Icon: DiamondOutlinedIcon,
      chipSx: {
        bgcolor: "#fef3c7",
        color: "#b45309",
        "& .MuiChip-icon": { ml: "6px", color: "#b45309 !important" },
      },
    };
  }
  if (t === "Luxury Goods") {
    return {
      Icon: WatchOutlinedIcon,
      chipSx: {
        bgcolor: "#fce7f3",
        color: "#be185d",
        "& .MuiChip-icon": { ml: "6px", color: "#be185d !important" },
      },
    };
  }
  return {
    Icon: CategoryOutlinedIcon,
    chipSx: {
      bgcolor: "#e0f2f1",
      color: "#0d9488",
      "& .MuiChip-icon": { ml: "6px", color: "#0d9488 !important" },
    },
  };
}

export default function ListNewAssetStep2NonRealEstate({
  draft,
  onDraftChange,
  nextStep,
  prevStep,
  categoryTitle = "",
  wizardStep = 2,
  wizardTotalSteps = 7,
}) {
  const theme = useTheme();
  const assetName = draft.assetName ?? "";
  const description = draft.description ?? "";
  const energyType = draft.energyType ?? "";
  const installedCapacityValue = draft.installedCapacityValue ?? "";
  const installedCapacityUnit = draft.installedCapacityUnit ?? "MW";
  const annualProductionMwh = draft.annualProductionMwh ?? "";
  const projectLocationSite = draft.projectLocationSite ?? "";
  const commodityType = draft.commodityType ?? "";
  const commodityQuantityValue = draft.commodityQuantityValue ?? "";
  const commodityQuantityUnit = draft.commodityQuantityUnit ?? "kg";
  const purityOrGrade = draft.purityOrGrade ?? "";
  const storageLocationVault = draft.storageLocationVault ?? "";
  const luxuryBrand = draft.luxuryBrand ?? "";
  const luxuryModel = draft.luxuryModel ?? "";
  const luxurySerialNumber = draft.luxurySerialNumber ?? "";
  const luxuryYearOfManufacture = draft.luxuryYearOfManufacture ?? "";
  const luxuryCondition = draft.luxuryCondition ?? "";

  const [errors, setErrors] = useState({});
  const meta = useMemo(() => categoryChipMeta(categoryTitle), [categoryTitle]);
  const ChipIcon = meta.Icon;
  const isRenewable = categoryTitle === "Renewable Energy";
  const isCommodities = categoryTitle === "Commodities";
  const isLuxuryGoods = categoryTitle === "Luxury Goods";

  const namePlaceholder = isLuxuryGoods
    ? "e.g., Patek Philippe Limited Edition"
    : isCommodities
      ? "e.g., Gold Reserve Vault 5"
      : isRenewable
        ? "e.g., Dubai Solar Farm Project"
        : "e.g., Asset name";

  const handleNext = () => {
    const newErrors = {};
    if (!String(assetName).trim()) newErrors.assetName = "Asset name is required.";
    if (!String(description).trim()) newErrors.description = "Description is required.";
    if (isRenewable && !String(energyType).trim()) newErrors.energyType = "Energy type is required.";
    if (isCommodities && !String(commodityType).trim()) newErrors.commodityType = "Commodity type is required.";
    if (isLuxuryGoods && !String(luxuryBrand).trim()) newErrors.luxuryBrand = "Brand is required.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    nextStep();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: 1100,
        mx: "auto",
        minHeight: "100vh",
        bgcolor: "#f9fafb",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            type="button"
            underline="none"
            onClick={prevStep}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: TEAL,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              p: 0,
              "&:hover": { color: TEAL_HOVER },
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
            Back
          </Link>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              icon={<ChipIcon sx={{ fontSize: "16px !important" }} />}
              label={categoryTitle || "Category"}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: 12,
                height: 26,
                ...meta.chipSx,
              }}
            />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>
              Step {wizardStep} of {wizardTotalSteps}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
          {Array.from({ length: wizardTotalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 999,
                bgcolor: i < wizardStep ? TEAL : "#e5e7eb",
              }}
            />
          ))}
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827", mb: 0.75 }}>
          Asset Details
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 3 }}>
          Provide complete information about your asset
        </Typography>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Box sx={filledFieldWrapSx(theme, !!assetName.trim())}>
              <RequiredLabel>Asset Name</RequiredLabel>
              <TextField
                fullWidth
                placeholder={namePlaceholder}
                value={assetName}
                onChange={(e) => { onDraftChange({ assetName: e.target.value }); setErrors((p) => ({ ...p, assetName: "" })); }}
                sx={{ mt: 1 }}
              />
              {errors.assetName && <FormHelperText error sx={{ mt: 0.5 }}>{errors.assetName}</FormHelperText>}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={filledFieldWrapSx(theme, !!description.trim())}>
              <RequiredLabel>Description</RequiredLabel>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Detailed description of your asset..."
                value={description}
                onChange={(e) => { onDraftChange({ description: e.target.value }); setErrors((p) => ({ ...p, description: "" })); }}
                sx={{ mt: 1 }}
              />
              {errors.description && <FormHelperText error sx={{ mt: 0.5 }}>{errors.description}</FormHelperText>}
            </Box>
          </Grid>
        </Grid>

        {isRenewable ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              bgcolor: "#ecfdf5",
              border: "1px solid #6ee7b7",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: "#d1fae5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#047857",
                }}
              >
                <ElectricBoltOutlinedIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#065f46" }}>
                Energy Asset Details
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!energyType)}>
                  <RequiredLabel>Energy Type</RequiredLabel>
                  <TextField
                    select
                    fullWidth
                    value={energyType}
                    onChange={(e) => { onDraftChange({ energyType: e.target.value }); setErrors((p) => ({ ...p, energyType: "" })); }}
                    sx={{ mt: 1, "& .MuiSelect-icon": { color: "#9ca3af" } }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v) =>
                        v ? v : <Typography sx={{ color: "#9ca3af" }}>Select Type</Typography>,
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select Type</em>
                    </MenuItem>
                    {ENERGY_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.energyType && <FormHelperText error sx={{ mt: 0.5 }}>{errors.energyType}</FormHelperText>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={filledFieldWrapSx(
                    theme,
                    !!String(installedCapacityValue ?? "").trim()
                  )}
                >
                  <OptionalLabel>Installed Capacity</OptionalLabel>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="stretch">
                    <TextField
                      fullWidth
                      placeholder="e.g. 50"
                      value={installedCapacityValue}
                      onChange={(e) => onDraftChange({ installedCapacityValue: e.target.value })}
                    />
                    <TextField
                      select
                      value={installedCapacityUnit}
                      onChange={(e) => onDraftChange({ installedCapacityUnit: e.target.value })}
                      sx={{ minWidth: 100, "& .MuiSelect-icon": { color: "#9ca3af" } }}
                    >
                      {CAPACITY_UNITS.map((u) => (
                        <MenuItem key={u} value={u}>
                          {u}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(annualProductionMwh ?? "").trim())}>
                  <OptionalLabel>Annual Production (MWh)</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. 80000"
                    value={annualProductionMwh}
                    onChange={(e) => onDraftChange({ annualProductionMwh: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(projectLocationSite ?? "").trim())}>
                  <OptionalLabel>Project Location / Site</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Al-Jouf Region, KSA"
                    value={projectLocationSite}
                    onChange={(e) => onDraftChange({ projectLocationSite: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        {isCommodities ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              bgcolor: "#fffbeb",
              border: "1px solid #fcd34d",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: "#fef3c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#b45309",
                }}
              >
                <DiamondOutlinedIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#92400e" }}>
                Commodity Details
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!commodityType)}>
                  <RequiredLabel>Commodity Type</RequiredLabel>
                  <TextField
                    select
                    fullWidth
                    value={commodityType}
                    onChange={(e) => { onDraftChange({ commodityType: e.target.value }); setErrors((p) => ({ ...p, commodityType: "" })); }}
                    sx={{ mt: 1, "& .MuiSelect-icon": { color: "#9ca3af" } }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v) =>
                        v ? (
                          v
                        ) : (
                          <Typography sx={{ color: "#9ca3af" }}>Select commodity</Typography>
                        ),
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select commodity</em>
                    </MenuItem>
                    {COMMODITY_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  {errors.commodityType && <FormHelperText error sx={{ mt: 0.5 }}>{errors.commodityType}</FormHelperText>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={filledFieldWrapSx(
                    theme,
                    !!String(commodityQuantityValue ?? "").trim()
                  )}
                >
                  <OptionalLabel>Weight / Quantity</OptionalLabel>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="stretch">
                    <TextField
                      fullWidth
                      placeholder="e.g. 500"
                      value={commodityQuantityValue}
                      onChange={(e) => onDraftChange({ commodityQuantityValue: e.target.value })}
                    />
                    <TextField
                      select
                      value={commodityQuantityUnit}
                      onChange={(e) => onDraftChange({ commodityQuantityUnit: e.target.value })}
                      sx={{ minWidth: 100, "& .MuiSelect-icon": { color: "#9ca3af" } }}
                    >
                      {COMMODITY_UNITS.map((u) => (
                        <MenuItem key={u} value={u}>
                          {u}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(purityOrGrade ?? "").trim())}>
                  <OptionalLabel>Purity / Grade</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. 99.9% - 24K"
                    value={purityOrGrade}
                    onChange={(e) => onDraftChange({ purityOrGrade: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(storageLocationVault ?? "").trim())}>
                  <OptionalLabel>Storage Location / Vault</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Riyadh Secure Vault, KSA"
                    value={storageLocationVault}
                    onChange={(e) => onDraftChange({ storageLocationVault: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        {isLuxuryGoods ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2,
              bgcolor: "#fdf2f8",
              border: "1px solid #f9a8d4",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: "#fce7f3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#be185d",
                }}
              >
                <WatchOutlinedIcon />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#9d174d" }}>
                Luxury Item Details
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(luxuryBrand ?? "").trim())}>
                  <RequiredLabel>Brand</RequiredLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Patek Philippe"
                    value={luxuryBrand}
                    onChange={(e) => { onDraftChange({ luxuryBrand: e.target.value }); setErrors((p) => ({ ...p, luxuryBrand: "" })); }}
                    sx={{ mt: 1 }}
                  />
                  {errors.luxuryBrand && <FormHelperText error sx={{ mt: 0.5 }}>{errors.luxuryBrand}</FormHelperText>}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(luxuryModel ?? "").trim())}>
                  <OptionalLabel>Model</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. Nautilus 5711/1A"
                    value={luxuryModel}
                    onChange={(e) => onDraftChange({ luxuryModel: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(luxurySerialNumber ?? "").trim())}>
                  <OptionalLabel>Serial Number</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. SN-4567891"
                    value={luxurySerialNumber}
                    onChange={(e) => onDraftChange({ luxurySerialNumber: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!String(luxuryYearOfManufacture ?? "").trim())}>
                  <OptionalLabel>Year of Manufacture</OptionalLabel>
                  <TextField
                    fullWidth
                    placeholder="e.g. 2020"
                    value={luxuryYearOfManufacture}
                    onChange={(e) => onDraftChange({ luxuryYearOfManufacture: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={filledFieldWrapSx(theme, !!luxuryCondition)}>
                  <OptionalLabel>Condition</OptionalLabel>
                  <TextField
                    select
                    fullWidth
                    value={luxuryCondition}
                    onChange={(e) => onDraftChange({ luxuryCondition: e.target.value })}
                    sx={{ mt: 1, "& .MuiSelect-icon": { color: "#9ca3af" } }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v) =>
                        v ? (
                          v
                        ) : (
                          <Typography sx={{ color: "#9ca3af" }}>Select condition</Typography>
                        ),
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select condition</em>
                    </MenuItem>
                    {LUXURY_CONDITIONS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        ) : null}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: "#d1d5db",
              color: "#374151",
              px: 3,
              "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
